import { CcdExportRowDto } from '@act/shared/data-transfer-objects';
import { createSlice } from '@reduxjs/toolkit';
import { useAppSelector } from 'redux/storeHooks';
import { resetRedux } from 'redux/actions/sharedActions';
import type { RootState } from 'redux/reemaStore.util';
import type { LoadingStatus } from 'redux/types/sharedTypes';
import { createApiThunk } from 'redux/utils/apiThunk';
import { ccdExportRequests } from 'redux/requests/ccdExport.requests';

export const getCcdExportList = createApiThunk(
  ccdExportRequests.getCcdExportList
);

interface CcdExportState {
  status: LoadingStatus;
  latestRequestId: string | null;
  rows: CcdExportRowDto[];
  count: number;
}

const initialState: CcdExportState = {
  status: 'initial',
  latestRequestId: null,
  rows: [],
  count: 0,
};

export const ccdExportSlice = createSlice({
  name: 'ccdExport',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, () => ({ ...initialState }))
      .addCase(getCcdExportList.pending, (state, action) => {
        state.status = 'loading';
        state.latestRequestId = action.meta.requestId;
      })
      .addCase(getCcdExportList.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestRequestId) {
          return;
        }
        state.status = 'success';
        state.rows = action.payload.rows ?? [];
        state.count = action.payload.count ?? 0;
      })
      .addCase(getCcdExportList.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestRequestId) {
          return;
        }
        state.status = 'failed';
        state.rows = [];
        state.count = 0;
      });
  },
});

export const ccdExportReducer = ccdExportSlice.reducer;

const selectCcdExportRows = (state: RootState) => state.ccdExport.rows;
const selectCcdExportCount = (state: RootState) => state.ccdExport.count;
const selectCcdExportStatus = (state: RootState) => state.ccdExport.status;

export const ccdExportAppSelectors = {
  useSelectRows: () => useAppSelector(selectCcdExportRows),
  useSelectCount: () => useAppSelector(selectCcdExportCount),
  useSelectStatus: () => useAppSelector(selectCcdExportStatus),
};
